import ClaimButton from "@components/inputs/ClaimButton";
import { closeIframeDialog } from "@components/utils/IframeDialog";
import { IframeDialogCloseReason, useStatsigTracking } from "@configs/statsig";
import { Paths } from "@constants";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { addCurrencySymbol } from "@utils/currency";
import logger from "@utils/logger";
import { getThemeAsset } from "@utils/theme";
import { isGameIframeDialogOpen } from "@utils/ui";
import { fAmount } from "kz-ui-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { MdCancel } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import styles from "./CashbackPrompt.module.css";

interface CashbackPromptProps {
  /**
   * Total claimable cashback amount
   */
  totalClaimable: number;

  /**
   * Whether the prompt is visible
   */
  visible: boolean;

  /**
   * Callback when prompt is dismissed
   */
  onDismiss: () => void;
}

// Animation constants
const ANIMATION_DURATION_MS = 300;
const APPEAR_ANIMATION_DURATION_MS = 400;
const UNMOUNT_DELAY_MS = 300;

// Swipe gesture constants
const SWIPE_THRESHOLD = 50; // Minimum distance in pixels to trigger dismiss
const MIN_SWIPE_DISTANCE = 10; // Minimum distance to start tracking swipe
const DISMISS_EXTENSION_MULTIPLIER = 2; // Multiply swipe distance for dismiss animation
const MIN_DISMISS_DISTANCE = 200; // Minimum distance for dismiss animation
const MAX_SWIPE_DISTANCE = 150; // Maximum swipe distance before damping becomes very strong
const DAMPING_FACTOR = 0.3; // Damping factor for resistance (0-1, lower = more resistance)
const OPACITY_START_DISTANCE = 30; // Distance at which opacity starts decreasing
const OPACITY_MIN_DISTANCE = 120; // Distance at which opacity reaches minimum
const OPACITY_MIN_VALUE = 0.3; // Minimum opacity value during swipe

// Position constants
const INITIAL_TRANSLATE_Y = -20; // Initial Y position before animation
const INITIAL_SCALE = 0.95; // Initial scale before animation
const FINAL_SCALE = 1; // Final scale after animation

// Easing functions
const APPEAR_EASING = "cubic-bezier(0.34, 1.56, 0.64, 1)";
const DISMISS_EASING = "ease-out";

const CashbackPrompt = ({ totalClaimable, visible, onDismiss }: CashbackPromptProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currency } = useMemberSettings();
  const { trackClaimCashbackMiniPrompt } = useStatsigTracking();
  const [isMounted, setIsMounted] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  // Swipe gesture state
  const touchStartY = useRef<number | null>(null);
  const touchStartX = useRef<number | null>(null);
  const [swipeOffsetX, setSwipeOffsetX] = useState(0);
  const [swipeOffsetY, setSwipeOffsetY] = useState(0);
  const [isDismissing, setIsDismissing] = useState(false);
  const [dismissTargetX, setDismissTargetX] = useState(0);
  const [dismissTargetY, setDismissTargetY] = useState(0);
  const dismissTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Resets all swipe-related states to initial values
   */
  const resetSwipeStates = useCallback(() => {
    setIsDismissing(false);
    setDismissTargetX(0);
    setDismissTargetY(0);
    setSwipeOffsetX(0);
    setSwipeOffsetY(0);
    touchStartX.current = null;
    touchStartY.current = null;
  }, []);

  /**
   * Calculates the Euclidean distance between two points
   */
  const calculateDistance = useCallback((x: number, y: number): number => {
    return Math.sqrt(x * x + y * y);
  }, []);

  /**
   * Calculates the dismiss target position based on swipe direction and distance
   */
  const calculateDismissTarget = useCallback(
    (deltaX: number, deltaY: number, swipeDistance: number): { targetX: number; targetY: number } => {
      const directionX = deltaX / swipeDistance;
      const directionY = deltaY / swipeDistance;
      const extendedDistance = Math.max(swipeDistance * DISMISS_EXTENSION_MULTIPLIER, MIN_DISMISS_DISTANCE);

      return {
        targetX: directionX * extendedDistance,
        targetY: directionY * extendedDistance,
      };
    },
    [],
  );

  /**
   * Applies damping effect to swipe distance to create resistance
   * Uses linear damping beyond MAX_SWIPE_DISTANCE for natural feel
   */
  const applyDamping = useCallback((distance: number): number => {
    if (distance <= MAX_SWIPE_DISTANCE) {
      return distance;
    }
    const excess = distance - MAX_SWIPE_DISTANCE;
    const dampedExcess = excess * DAMPING_FACTOR;
    return MAX_SWIPE_DISTANCE + dampedExcess;
  }, []);

  /**
   * Calculates opacity based on swipe distance
   * Opacity decreases gradually as swipe distance increases
   */
  const calculateOpacity = useCallback((swipeDistance: number): number => {
    if (swipeDistance < OPACITY_START_DISTANCE) {
      return 1;
    }
    if (swipeDistance >= OPACITY_MIN_DISTANCE) {
      return OPACITY_MIN_VALUE;
    }
    const progress = (swipeDistance - OPACITY_START_DISTANCE) / (OPACITY_MIN_DISTANCE - OPACITY_START_DISTANCE);
    return 1 - progress * (1 - OPACITY_MIN_VALUE);
  }, []);

  const formattedAmount = useMemo(() => addCurrencySymbol(fAmount(totalClaimable, { decimals: 2 })), [totalClaimable]);

  useEffect(() => {
    if (visible) {
      logger._console.log("[CashbackPrompt] Prompt became visible", {
        totalClaimable,
        formattedAmount,
      });

      resetSwipeStates();
      setIsMounted(true);

      // Trigger animation after DOM is ready using double RAF for reliable timing
      const animationTimer = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setShouldAnimate(true);
        });
      });

      return () => cancelAnimationFrame(animationTimer);
    } else {
      logger._console.debug("[CashbackPrompt] Prompt hidden");
      setShouldAnimate(false);
      resetSwipeStates();

      // Delay unmounting to allow exit animation to complete
      const unmountTimer = setTimeout(() => {
        setIsMounted(false);
      }, UNMOUNT_DELAY_MS);

      return () => clearTimeout(unmountTimer);
    }
  }, [visible, totalClaimable, formattedAmount, resetSwipeStates]);

  const handleClaim = useCallback(() => {
    logger._console.log("[CashbackPrompt] User clicked Claim", {
      totalClaimable,
      formattedAmount,
    });

    trackClaimCashbackMiniPrompt({
      totalClaimable,
      currency: currency,
      isInGame: isGameIframeDialogOpen(),
    });

    closeIframeDialog({ reason: IframeDialogCloseReason.CASHBACK_CLAIM });
    navigate(Paths.PRIVATE.PROMOTION_CASHBACK, { replace: true });
  }, [navigate, totalClaimable, formattedAmount, trackClaimCashbackMiniPrompt, currency]);

  const handleDismiss = useCallback(() => {
    logger._console.debug("[CashbackPrompt] User dismissed prompt", {
      totalClaimable,
      formattedAmount,
    });

    onDismiss();
  }, [onDismiss, totalClaimable, formattedAmount]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartY.current = touch.clientY;
    touchStartX.current = touch.clientX;
    setSwipeOffsetX(0);
    setSwipeOffsetY(0);
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartY.current === null || touchStartX.current === null) return;

      const touch = e.touches[0];
      if (!touch) return;

      const deltaY = touch.clientY - touchStartY.current;
      const deltaX = touch.clientX - touchStartX.current;
      const rawSwipeDistance = calculateDistance(deltaX, deltaY);

      // Only track swipe if it's significant enough to prevent accidental triggers
      if (rawSwipeDistance > MIN_SWIPE_DISTANCE) {
        // Apply damping to create resistance effect
        const dampedDistance = applyDamping(rawSwipeDistance);
        const directionX = deltaX / rawSwipeDistance;
        const directionY = deltaY / rawSwipeDistance;

        setSwipeOffsetX(directionX * dampedDistance);
        setSwipeOffsetY(directionY * dampedDistance);
      }
    },
    [calculateDistance, applyDamping],
  );

  const handleTouchEnd = useCallback(() => {
    if (touchStartY.current === null || touchStartX.current === null) return;

    const swipeDistance = calculateDistance(swipeOffsetX, swipeOffsetY);

    if (swipeDistance > SWIPE_THRESHOLD) {
      const { targetX, targetY } = calculateDismissTarget(swipeOffsetX, swipeOffsetY, swipeDistance);

      setDismissTargetX(targetX);
      setDismissTargetY(targetY);
      setIsDismissing(true);
      touchStartX.current = null;
      touchStartY.current = null;

      // Animate to target position, then call handleDismiss
      dismissTimeoutRef.current = setTimeout(() => {
        handleDismiss();
        resetSwipeStates();
        dismissTimeoutRef.current = null;
      }, ANIMATION_DURATION_MS);
    } else {
      resetSwipeStates();
    }
  }, [swipeOffsetX, swipeOffsetY, handleDismiss, calculateDistance, calculateDismissTarget, resetSwipeStates]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current);
        dismissTimeoutRef.current = null;
      }
    };
  }, []);

  const isSwiping = swipeOffsetX !== 0 || swipeOffsetY !== 0;
  const baseTranslateY = shouldAnimate && !isDismissing ? 0 : INITIAL_TRANSLATE_Y;
  const baseScale = shouldAnimate && !isDismissing ? FINAL_SCALE : INITIAL_SCALE;

  const totalTranslateX = isDismissing ? dismissTargetX : swipeOffsetX;
  const totalTranslateY = isDismissing ? baseTranslateY + dismissTargetY : baseTranslateY + swipeOffsetY;

  // Calculate opacity based on swipe distance (only when swiping)
  const swipeDistance = isSwiping ? calculateDistance(swipeOffsetX, swipeOffsetY) : 0;
  const swipeOpacity = isSwiping && !isDismissing ? calculateOpacity(swipeDistance) : 1;

  const content = (
    <div className="fixed start-0 top-12 z-[10001] w-full">
      <div className="absolute start-1/2 w-max max-w-md -translate-start-1/2">
        <img
          className="pointer-events-none absolute start-1/2 top-0 z-[1] h-[35px] w-[180px] -translate-y-4 object-cover object-bottom -translate-start-1/2"
          src={getThemeAsset("/images/mini-prompt/cashback-highlight.png")}
          style={{
            opacity: isDismissing ? 0 : visible && shouldAnimate ? swipeOpacity : 0,
          }}
        />
        <div
          className="max-w-md origin-center transform"
          style={{
            pointerEvents: visible && !isDismissing ? "auto" : "none",
            transform: `translate(calc(${totalTranslateX}px), ${totalTranslateY}px) scale(${baseScale})`,
            transition:
              !isSwiping && !isDismissing
                ? `transform ${APPEAR_ANIMATION_DURATION_MS}ms ${APPEAR_EASING}, opacity ${APPEAR_ANIMATION_DURATION_MS}ms ease-out`
                : isDismissing
                  ? `transform ${ANIMATION_DURATION_MS}ms ${DISMISS_EASING}, opacity ${ANIMATION_DURATION_MS}ms ${DISMISS_EASING}`
                  : "none",
            opacity: isDismissing ? 0 : visible && shouldAnimate ? swipeOpacity : 0,
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Main Banner */}
          <div className="relative w-full rounded-[40px] bg-grdLow shadow-sm">
            {/* Shine Overlay */}
            <div className="pointer-events-none absolute start-0 top-0 z-[6] h-full w-full overflow-hidden rounded-[40px]">
              <div className={`absolute start-0 top-0 h-full w-full will-change-transform ${styles.shine}`}></div>
            </div>

            <div className="flex w-full items-center justify-center gap-4 px-5 py-3">
              <div className="flex shrink-0 flex-col items-center justify-center">
                <div className="text-xs text-content-base">{t("cashback unlocked")}</div>
                <div className="text-center text-base font-bold text-content-base">{formattedAmount}</div>
              </div>
              <div className="flex shrink-0 items-center justify-center">
                <ClaimButton
                  onClick={handleClaim}
                  withCoin={true}
                  withShine={true}
                  claimable={true}
                  text={t("claim cashback")}
                  loading={false}
                  aria-label="Claim cashback prompt button"
                  size="sm"
                  wrapperClassName="!w-fit !h-[26px] !inline-block"
                  buttonClassName="!w-fit px-3"
                  buttonWrapperClassName="!w-fit !relative"
                  classes={{
                    text: "",
                  }}
                />
              </div>
            </div>

            {/* Dismiss Button - Bottom Center */}
            <button
              onClick={handleDismiss}
              className="absolute -bottom-3 start-1/2 flex h-5 w-5 items-center justify-center rounded-full bg-accentBrand-300 -translate-start-1/2"
              aria-label="Dismiss"
            >
              <MdCancel className="h-5 w-5 text-accentBrand-950" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isMounted) {
    return null;
  }

  // Check if document.body exists before creating portal
  if (!document.body) {
    logger._console.debug("[CashbackPrompt] document.body not available, cannot render portal");
    return null;
  }

  try {
    return createPortal(content, document.body);
  } catch (error) {
    logger._console.error("[CashbackPrompt] Failed to create portal", error);
    return null;
  }
};

export default CashbackPrompt;
